import { useMemo } from 'react';

import { buildDetailsNarration } from '../services/build-details-narration';
import { buildTimelineNarration } from '../services/build-timeline-narration';
import { deriveTimelineStage } from '../services/derive-timeline-stage';
import { useExperimentChatbotStore } from '../store/experiment-chatbot.store';
import type { AnalyticsSensitivity } from '../types/analytics-controls.types';
import type { ChatbotMessage, ChatbotView, TimelineStage } from '../types/chatbot-message.types';

import { useAnalyticsAgentSummary } from './use-experiment-analytics';
import { useExperimentCategoryPerformance } from './use-experiment-category-performance';
import {
  useExperiment,
  useExperimentArmComparison,
  useExperimentInStockTrends,
  useExperimentKpis,
  useExperimentTimeline,
} from './use-experiments';

interface UseExperimentChatbotMessagesResult {
  /** Cumulative narration for the requested view. */
  messages: ChatbotMessage[];
  /** True while the queries needed for the current view are still resolving for the first time. */
  isLoading: boolean;
  /** True when the experiment fetch errored — the panel surfaces a retry hint. */
  isError: boolean;
  /** Stage derived from real data (timeline view only); null otherwise or before experiment loads. */
  derivedStage: TimelineStage | null;
  /** Stage actually used to render — equals the dev override when set, otherwise `derivedStage`. Only meaningful in timeline view. */
  effectiveStage: TimelineStage;
}

interface UseExperimentChatbotMessagesOptions {
  /** Fixed outlier sensitivity. Threaded into `useAnalyticsAgentSummary`. */
  sensitivity?: AnalyticsSensitivity;
}

/**
 * Composes the experiment-detail queries into a chatbot message list. The
 * `view` arg mirrors the active main + sub-tab on the Experiment Details page:
 * `timeline` runs `buildTimelineNarration`; `performance` / `category` / `health`
 * run `buildDetailsNarration` (`category` → the category-mode Performance
 * narration). Subscriptions to ArmComparison + InStockTrends are
 * always live so switching tabs feels instant; TanStack Query dedupes against
 * the existing detail-tab subscriptions.
 *
 * When `view === 'performance'` and the analytics agent-summary is available,
 * its `headline` + `notes` override the three hardcoded intro narrative
 * messages so the chatbot opens with sensitivity-tuned live numbers. On 404
 * or any error the legacy scripted intro renders unchanged.
 */
export function useExperimentChatbotMessages(
  experimentId: string,
  view: ChatbotView,
  options: UseExperimentChatbotMessagesOptions = {},
): UseExperimentChatbotMessagesResult {
  const devOverride = useExperimentChatbotStore((s) => s.devStageOverride);

  const experimentQuery = useExperiment(experimentId);
  const timelineQuery = useExperimentTimeline(experimentId);
  const kpisQuery = useExperimentKpis(experimentId);
  const armComparisonQuery = useExperimentArmComparison(experimentId);
  const inStockTrendsQuery = useExperimentInStockTrends(experimentId);
  // Only subscribe to category data when actually rendering the Category-view
  // narration; saves a request on the default Performance / Health views.
  const categoryPerformanceQuery = useExperimentCategoryPerformance(experimentId, 'summary', {
    enabled: view === 'category',
  });
  // Analytics-derived headline + notes; only relevant on the Performance view.
  const agentSummaryQuery = useAnalyticsAgentSummary(
    experimentId,
    options.sensitivity ? { sensitivity_level: options.sensitivity } : undefined,
    /* enabled */ view === 'performance',
  );

  const experiment = experimentQuery.data;
  const timeline = timelineQuery.data;
  const kpis = kpisQuery.data;
  const armComparison = armComparisonQuery.data;
  const inStockTrends = inStockTrendsQuery.data;
  const categoryPerformance = categoryPerformanceQuery.data;

  const derivedStage = useMemo<TimelineStage | null>(() => {
    if (!experiment) return null;
    return deriveTimelineStage({ experiment, kpis, timeline });
  }, [experiment, kpis, timeline]);

  const effectiveStage: TimelineStage = devOverride ?? derivedStage ?? 'day-0';

  const agentSummary = agentSummaryQuery.data;

  const messages = useMemo<ChatbotMessage[]>(() => {
    if (view === 'timeline') {
      return buildTimelineNarration({ stage: effectiveStage, experiment, kpis, timeline });
    }
    // Map the chatbot view onto the narration builder's (view, mode) inputs:
    // the Category sub-tab renders the category-mode Performance narration.
    return buildDetailsNarration({
      view: view === 'health' ? 'health' : 'performance',
      performanceMode: view === 'category' ? 'category' : 'default',
      experiment,
      kpis,
      armComparison,
      inStockTrends,
      categoryPerformance,
      agentSummary,
    });
  }, [
    view,
    effectiveStage,
    experiment,
    kpis,
    timeline,
    armComparison,
    inStockTrends,
    categoryPerformance,
    agentSummary,
  ]);

  // Loading is view-specific so the skeleton only appears while the queries
  // that actually feed the rendered narration are still resolving.
  const isLoading =
    view === 'timeline'
      ? experimentQuery.isLoading || timelineQuery.isLoading || kpisQuery.isLoading
      : view === 'category'
        ? experimentQuery.isLoading || categoryPerformanceQuery.isLoading
        : view === 'performance'
          ? experimentQuery.isLoading || armComparisonQuery.isLoading || kpisQuery.isLoading
          : experimentQuery.isLoading || inStockTrendsQuery.isLoading;

  return {
    messages,
    isLoading,
    isError: experimentQuery.isError,
    derivedStage,
    effectiveStage,
  };
}
